/**
 * Question types and their definitions
 */

/**
 * Question types supported by the inspection system
 */
export type QuestionType =
  | 'rating_5_point' // Excellent, Good, Fair, Poor, N/A
  | 'rating_3_point' // Good, Fair, Poor (with N/A)
  | 'pass_fail' // Pass, Fail (with N/A)
  | 'ok_deficient' // OK, Deficient (with N/A)
  | 'condition_action' // Condition notes + Action taken
  | 'yes_no' // Yes, No (with optional N/A)
  | 'checklist' // Checkbox (complete/incomplete)
  | 'text_only' // Free-form text response
  | 'numeric' // Numeric input with optional range
  | 'date' // Date picker
  | 'select' // Single select from options
  | 'multi_select' // Multi-select from options
  | 'observation'; // Freeform: inspector provides label + notes/photos

/**
 * Configuration for question types that require it
 */
export type QuestionConfig =
  | NumericQuestionConfig
  | SelectQuestionConfig
  | DateQuestionConfig;

/** Config for numeric question type */
export interface NumericQuestionConfig {
  type: 'numeric';
  /** Minimum allowed value */
  min?: number;
  /** Maximum allowed value */
  max?: number;
  /** Unit label (e.g., "°F", "PSI", "sq ft") */
  unit?: string;
  /** Step increment for input controls */
  step?: number;
  /** Number of decimal places */
  precision?: number;
}

/** Config for select and multi_select question types */
export interface SelectQuestionConfig {
  type: 'select' | 'multi_select';
  /** Available options */
  options: SelectOption[];
}

/** Config for date question type */
export interface DateQuestionConfig {
  type: 'date';
  /** Minimum allowed date (ISO 8601) */
  minDate?: string;
  /** Maximum allowed date (ISO 8601) */
  maxDate?: string;
  /** Include time selection */
  includeTime?: boolean;
}

export interface SelectOption {
  /** Stable value identifier */
  value: string;

  /** Display label */
  label: string;

  /** Whether this option indicates a deficiency */
  isDeficient: boolean;

  /** Display order */
  order: number;
}

/**
 * Additional field definition for complex question types
 */
export interface AdditionalField {
  /** Field key */
  key: string;
  /** Display label */
  label: string;
  /** Input type */
  type: 'text' | 'number' | 'date';
  /** Whether this field is required */
  required?: boolean;
}

/**
 * Question type definition (metadata about a question type)
 */
export interface QuestionTypeDefinition {
  /** Question type identifier */
  type: QuestionType;

  /** Human-readable display name */
  displayName: string;

  /** Optional description */
  description?: string;

  /** Predefined options (for built-in types) */
  options: SelectOption[];

  /** Whether notes/comments are supported */
  supportsNotes: boolean;

  /** Whether photos are supported */
  supportsPhotos: boolean;

  /** Additional fields for complex types */
  additionalFields?: AdditionalField[];
}

/**
 * Built-in question type definitions catalog
 */
export const QUESTION_TYPE_DEFINITIONS: Record<
  QuestionType,
  QuestionTypeDefinition
> = {
  rating_5_point: {
    type: 'rating_5_point',
    displayName: '5-Point Rating',
    options: [
      { value: 'excellent', label: 'Excellent', isDeficient: false, order: 0 },
      { value: 'good', label: 'Good', isDeficient: false, order: 1 },
      { value: 'fair', label: 'Fair', isDeficient: true, order: 2 },
      { value: 'poor', label: 'Poor', isDeficient: true, order: 3 },
      { value: 'na', label: 'N/A', isDeficient: false, order: 4 },
    ],
    supportsNotes: true,
    supportsPhotos: true,
  },

  rating_3_point: {
    type: 'rating_3_point',
    displayName: '3-Point Rating',
    options: [
      { value: 'good', label: 'Good', isDeficient: false, order: 0 },
      { value: 'fair', label: 'Fair', isDeficient: true, order: 1 },
      { value: 'poor', label: 'Poor', isDeficient: true, order: 2 },
      { value: 'na', label: 'N/A', isDeficient: false, order: 3 },
    ],
    supportsNotes: true,
    supportsPhotos: true,
  },

  pass_fail: {
    type: 'pass_fail',
    displayName: 'Pass/Fail',
    options: [
      { value: 'pass', label: 'Pass', isDeficient: false, order: 0 },
      { value: 'fail', label: 'Fail', isDeficient: true, order: 1 },
      { value: 'na', label: 'N/A', isDeficient: false, order: 2 },
    ],
    supportsNotes: true,
    supportsPhotos: true,
  },

  ok_deficient: {
    type: 'ok_deficient',
    displayName: 'OK/Deficient',
    options: [
      { value: 'ok', label: 'OK', isDeficient: false, order: 0 },
      { value: 'deficient', label: 'Deficient', isDeficient: true, order: 1 },
      { value: 'na', label: 'N/A', isDeficient: false, order: 2 },
    ],
    supportsNotes: true,
    supportsPhotos: true,
  },

  condition_action: {
    type: 'condition_action',
    displayName: 'Condition & Action',
    options: [],
    supportsNotes: true,
    supportsPhotos: true,
    additionalFields: [
      { key: 'condition', label: 'Condition/Notes', type: 'text' },
      { key: 'action', label: 'Action Taken', type: 'text' },
    ],
  },

  yes_no: {
    type: 'yes_no',
    displayName: 'Yes/No',
    options: [
      { value: 'yes', label: 'Yes', isDeficient: false, order: 0 },
      { value: 'no', label: 'No', isDeficient: true, order: 1 },
      { value: 'na', label: 'N/A', isDeficient: false, order: 2 },
    ],
    supportsNotes: true,
    supportsPhotos: true,
  },

  checklist: {
    type: 'checklist',
    displayName: 'Checklist',
    options: [
      { value: 'complete', label: 'Complete', isDeficient: false, order: 0 },
      {
        value: 'incomplete',
        label: 'Incomplete',
        isDeficient: true,
        order: 1,
      },
      { value: 'na', label: 'N/A', isDeficient: false, order: 2 },
    ],
    supportsNotes: true,
    supportsPhotos: true,
  },

  text_only: {
    type: 'text_only',
    displayName: 'Text Response',
    options: [],
    supportsNotes: false, // Notes ARE the response
    supportsPhotos: true,
  },

  numeric: {
    type: 'numeric',
    displayName: 'Numeric',
    options: [],
    supportsNotes: true,
    supportsPhotos: true,
  },

  date: {
    type: 'date',
    displayName: 'Date',
    options: [],
    supportsNotes: true,
    supportsPhotos: false,
  },

  select: {
    type: 'select',
    displayName: 'Single Select',
    options: [], // Defined in questionConfig
    supportsNotes: true,
    supportsPhotos: true,
  },

  multi_select: {
    type: 'multi_select',
    displayName: 'Multi-Select',
    options: [], // Defined in questionConfig
    supportsNotes: true,
    supportsPhotos: true,
  },

  observation: {
    type: 'observation',
    displayName: 'Observation',
    description: 'Freeform finding - inspector provides label and details',
    options: [],
    supportsNotes: true, // Notes capture the observation details
    supportsPhotos: true,
    additionalFields: [
      {
        key: 'observationLabel',
        label: 'What did you observe?',
        type: 'text',
        required: true,
      },
    ],
  },
};
